import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { useGetFailedDocs } from '@/api/queries/useGetFailedDocs';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { FailedDocument } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const fallbackTableData: FailedDocument[] = [];
const columnHelper = createColumnHelper<FailedDocument>();

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Name</span>,
    cell: (info) => <span className="text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="text-gray-500">{info.getValue() || '—'}</span>,
  }),
  columnHelper.accessor('size', {
    header: () => <span>Size</span>,
    cell: (info) => <span className="text-gray-500">{formatFileSize(info.getValue())}</span>,
  }),
  columnHelper.accessor('uploadDate', {
    header: () => <span>Upload Date</span>,
    cell: (info) => <span className="text-gray-500">{formatDate(info.getValue(), true, true)}</span>,
  }),
  columnHelper.accessor('pipelineErrorType', {
    header: () => <span>Error Reason</span>,
    cell: (info) => <span className="text-red-600">{info.getValue()}</span>,
  }),
];

export function FailedDocuments() {
  const { matterId, runId } = useParams();
  if (!matterId || !runId) return null;

  const [globalFilter, setGlobalFilter] = useState('');
  const { data: runs } = useGetMatterRuns(matterId);
  const { data: failedDocs, isLoading: isLoadingFailedDocs } = useGetFailedDocs(runId);

  const selectedRun = runs?.find((r) => r.id === runId);

  const table = useReactTable({
    data: failedDocs ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <div className="mb-4 flex items-center justify-between gap-2">
          <Button variant="outline" size="sm" asChild className="h-auto px-2 py-1 text-gray-600 hover:text-gray-800">
            <Link to={`/matters/${matterId}/settings/run-history`}>
              <Icon className="lucide--arrow-left mr-1 h-4 w-4" />
              Back to Run History
            </Link>
          </Button>

          <span className="text-sm text-gray-500">Run ID: {runId}</span>
        </div>
        <h2 className="mb-1 text-base font-semibold">Failed Documents</h2>
        <p className="text-sm text-gray-500">
          {selectedRun
            ? `Documents that failed to process in the run started ${formatDate(selectedRun.createdDate, true, true)}`
            : 'Documents that failed to process'}
        </p>
      </div>

      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        plural="failed documents"
        loading={isLoadingFailedDocs}
        className="p-0"
      />
    </div>
  );
}
